import { useCallback, useEffect, useState } from "react";
import { Loader2, Check, X, RefreshCw, MapPin, ExternalLink, Download } from "lucide-react";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { toast } from "../../../components/ui/sonner";

const STATUSES = ["PENDING", "APPROVED", "REJECTED", ""];

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function MappingTab() {
  const { authFetch, downloadCsv } = useAdminAuth();
  const [status, setStatus] = useState("PENDING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      const path = `/api/admin/export/mapping.csv${params.toString() ? `?${params}` : ""}`;
      const name = await downloadCsv(path, "geonetix-mapping.csv");
      toast("CSV downloaded", { description: name });
    } catch (e) {
      toast("Export failed", { description: e.message });
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(
    async (st = status) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (st) params.set("status", st);
        params.set("limit", "100");
        const data = await authFetch(`/api/admin/mapping?${params.toString()}`);
        setItems(data.items || []);
      } catch {
        /* handled */
      } finally {
        setLoading(false);
      }
    },
    [authFetch, status]
  );

  useEffect(() => {
    load(status);
  }, [status, load]);

  const act = async (id, action) => {
    setActing(id + action);
    try {
      const res = await authFetch(`/api/admin/mapping/${action}`, {
        method: "POST",
        body: JSON.stringify({ mapping_id: id }),
      });
      const suffix = res.credited ? ` · credited ${formatINR(res.credited)}` : "";
      toast(`Mapping ${res.status}${suffix}`);
      load(status);
    } catch (e) {
      toast("Action failed", { description: e.message });
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-full border border-white/10 bg-black/20 p-1">
          {STATUSES.map((s) => (
            <button
              key={s || "all"}
              data-testid={`mapping-filter-${(s || "all").toLowerCase()}`}
              onClick={() => setStatus(s)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors ${
                status === s ? "bg-[#00B4D8] text-[#04140a]" : "text-slate-300"
              }`}
            >
              {s || "all"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="mapping-export-csv"
            onClick={doExport}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-slate-200 hover:brightness-110 disabled:opacity-60"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} CSV
          </button>
          <button onClick={() => load(status)} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#00B4D8]">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="mapping-empty" className="text-slate-500 text-center py-16">
          No {status ? status.toLowerCase() : ""} mapping submissions.
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} data-testid={`mapping-item-${m.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <div className="font-semibold text-white">{m.user_full_name || m.user_id}</div>
                  <div className="text-xs text-slate-500">{m.user_email}</div>
                </div>
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                    m.status === "APPROVED"
                      ? "border-[#4CAF50]/40 text-[#4CAF50] bg-[#4CAF50]/10"
                      : m.status === "REJECTED"
                      ? "border-red-400/40 text-red-300 bg-red-400/10"
                      : "border-amber-300/40 text-amber-300 bg-amber-300/10"
                  }`}
                >
                  {m.status || "PENDING"}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <Stat label="Distance" value={`${Number(m.distance_km || 0).toFixed(2)} km`} />
                <Stat label="Earnings" value={formatINR(m.earnings)} accent="text-[#4CAF50]" />
                <Stat label="Submitted" value={m.submitted_at ? String(m.submitted_at).slice(0, 10) : "—"} />
                <Stat label="Path points" value={Array.isArray(m.path) ? m.path.length : "—"} />
              </div>
              {m.video_link && (
                <a
                  href={m.video_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[#00B4D8] hover:underline mb-3"
                >
                  <ExternalLink size={12} /> View video
                </a>
              )}
              {String(m.status || "").toUpperCase() === "PENDING" && (
                <div className="flex gap-2 justify-end">
                  <button
                    data-testid={`mapping-reject-${m.id}`}
                    onClick={() => act(m.id, "reject")}
                    disabled={acting === m.id + "reject"}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-400/50 text-red-300 px-4 py-2 text-xs font-semibold hover:bg-red-400/10 disabled:opacity-60"
                  >
                    <X size={13} /> Reject
                  </button>
                  <button
                    data-testid={`mapping-approve-${m.id}`}
                    onClick={() => act(m.id, "approve")}
                    disabled={acting === m.id + "approve"}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#4CAF50] px-4 py-2 text-xs font-semibold text-[#04140a] hover:brightness-110 disabled:opacity-60"
                  >
                    <Check size={13} /> Approve &amp; credit
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
        <MapPin size={10} /> {label}
      </div>
      <div className={`font-semibold font-mono-gx text-sm ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}
